import { useEffect, useState } from "react";
import { tv } from "tailwind-variants";
import { Button, Modal, Flex, Checkbox, IconButton } from "@bdo/cornerstone-ui";
import { RiCloseLine } from "@remixicon/react";
import { useFeatureFlags } from "@/hooks";

export interface WorkflowDefinitionFilter {
  workflowDefinitionName: string;
  version: string;
}

interface FilterModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialStatusFilter: string[];
  initialWorkflowDefinitionFilter: WorkflowDefinitionFilter[];
  workflowDefinitions: WorkflowDefinitionFilter[];
  onApplyFilters: (
    statusFilter: string[],
    workflowDefinitionFilter: WorkflowDefinitionFilter[],
  ) => void;
}

export function FilterModal({
  isOpen,
  onClose,
  initialStatusFilter,
  initialWorkflowDefinitionFilter,
  workflowDefinitions,
  onApplyFilters,
}: Readonly<FilterModalProps>) {
  const { isArchiveEnabled } = useFeatureFlags();
  const [selectedModalFilter, setSelectedModalFilter] = useState<
    "status" | "workflow"
  >("workflow");
  const [statusFilter, setStatusFilter] =
    useState<string[]>(initialStatusFilter);
  const [workflowDefinitionFilter, setWorkflowDefinitionFilter] = useState<
    WorkflowDefinitionFilter[]
  >(initialWorkflowDefinitionFilter);

  useEffect(() => {
    setStatusFilter(initialStatusFilter);
    setWorkflowDefinitionFilter(initialWorkflowDefinitionFilter);
  }, [initialStatusFilter, initialWorkflowDefinitionFilter]);

  function handleUpdate(
    filterType: "status" | "workflow",
    filter: string | WorkflowDefinitionFilter,
    isSelected: boolean,
  ) {
    if (filterType === "status") {
      setStatusFilter((prev) => {
        if (isSelected) {
          return prev.concat(filter as string);
        } else {
          return prev.filter((f) => f !== (filter as string));
        }
      });
    } else {
      setWorkflowDefinitionFilter((prev) => {
        const selectedFilter = filter as WorkflowDefinitionFilter;
        if (isSelected) {
          if (
            prev.some(
              (f) =>
                f.workflowDefinitionName ===
                  selectedFilter.workflowDefinitionName &&
                f.version === selectedFilter.version,
            )
          ) {
            return prev;
          }

          return prev.concat(selectedFilter);
        } else {
          return prev.filter(
            (f) =>
              !(
                f.workflowDefinitionName ===
                  selectedFilter.workflowDefinitionName &&
                f.version === selectedFilter.version
              ),
          );
        }
      });
    }
  }

  function handleClose() {
    onClose();
    setSelectedModalFilter("workflow");
  }

  function handleSubmit() {
    onApplyFilters(statusFilter, workflowDefinitionFilter);
    handleClose();
  }

  const styles = tv({
    slots: {
      modalContainer: "w-full max-w-[1000px]",
      contentContainer: "h-full min-h-[300px] max-h-[500px] ",
      closeButton: "absolute right-0",
      sidebar: "max-w-[200px] border-r border-shade-lightest",
      heading: "text-mono-darker font-bold text-lg p-md",
      selector:
        "p-sm text-left flex justify-between items-center hover:bg-mono-lightest transition-colors ease-in-out",
      footer: "px-md py-sm border-t border-shade-lightest",
    },
    variants: {
      isSelected: {
        true: {
          selector: "bg-mono-lighter",
        },
      },
    },
  });

  const {
    modalContainer,
    contentContainer,
    closeButton,
    sidebar,
    heading,
    selector,
    footer,
  } = styles();

  return (
    <Modal
      bdoMainContainerClassName={modalContainer()}
      isOpen={isOpen}
      setIsOpen={handleClose}
      hasPadding={false}
    >
      <Flex className={contentContainer()}>
        <Flex direction="column" className={sidebar()}>
          <h2 className={heading()}>All filters</h2>
          <button
            className={selector({
              isSelected: selectedModalFilter === "status",
            })}
            onClick={() => setSelectedModalFilter("status")}
            data-testid="filter-modal-status-button"
          >
            Status
          </button>
          <button
            className={selector({
              isSelected: selectedModalFilter === "workflow",
            })}
            onClick={() => setSelectedModalFilter("workflow")}
            data-testid="filter-modal-workflow-button"
          >
            Workflow type
          </button>
        </Flex>
        <Flex className="p-2xl">
          {selectedModalFilter === "status" && (
            <Flex direction="column" gap="lg">
              <Checkbox
                value="RequiresAttention"
                label="Requires attention"
                isSelected={statusFilter.includes("RequiresAttention")}
                onChange={(isSelected) =>
                  handleUpdate("status", "RequiresAttention", isSelected)
                }
                data-testid="status-filter-modal-requires-attention"
              />
              <Checkbox
                value="InProgress"
                label="In progress"
                isSelected={statusFilter.includes("InProgress")}
                onChange={(isSelected) =>
                  handleUpdate("status", "InProgress", isSelected)
                }
                data-testid="status-filter-modal-in-progress"
              />
              <Checkbox
                value="Completed"
                label="Completed"
                isSelected={statusFilter.includes("Completed")}
                onChange={(isSelected) =>
                  handleUpdate("status", "Completed", isSelected)
                }
                data-testid="status-filter-modal-completed"
              />
              {isArchiveEnabled && (
                <Checkbox
                  value="Archived"
                  label="Archived"
                  isSelected={statusFilter.includes("Archived")}
                  onChange={(isSelected) =>
                    handleUpdate("status", "Archived", isSelected)
                  }
                  data-testid="status-filter-modal-archived"
                />
              )}
            </Flex>
          )}
          {selectedModalFilter === "workflow" && (
            <Flex direction="column" gap="lg" wrap="wrap">
              {workflowDefinitions.map((w) => (
                <Checkbox
                  key={`${w.workflowDefinitionName}-${w.version}`}
                  value={`${w.workflowDefinitionName}-${w.version}`}
                  label={`${w.workflowDefinitionName} (v${w.version})`}
                  isSelected={workflowDefinitionFilter.some(
                    (f) =>
                      f.workflowDefinitionName === w.workflowDefinitionName &&
                      f.version === w.version,
                  )}
                  onChange={(isSelected) =>
                    handleUpdate("workflow", w, isSelected)
                  }
                  data-testid={`workflow-filter-modal-${w.workflowDefinitionName}-${w.version}`}
                />
              ))}
            </Flex>
          )}
        </Flex>
        <IconButton
          icon={<RiCloseLine />}
          className={closeButton()}
          onClick={handleClose}
          aria-label="Close modal"
        />
      </Flex>
      <Flex className={footer()} justify="between">
        <Button
          size="md"
          onClick={handleClose}
          data-testid="filter-modal-cancel"
        >
          Cancel
        </Button>
        <Button
          size="md"
          variant="primary"
          onClick={handleSubmit}
          data-testid="filter-modal-apply"
        >
          Apply filters
        </Button>
      </Flex>
    </Modal>
  );
}
